import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Maximize2, Minimize2, X } from "lucide-react";
import { RootState } from "../../redux/rootReducer";
import {
    AssignmentKind,
    getCachedAssignment,
    loadAssignment,
} from "../../services/assignmentContent";
import PdfViewer from "./PdfViewer";
import { HTML_SANDBOX, prepareHtmlForViewer } from "./htmlViewer";

/**
 * In-app assignment preview, matching the teacher resource viewer:
 * the file is loaded through the API, PDFs are drawn on the page, and HTML runs in a sandbox.
 */
export default function AssignmentViewer({
    programId,
    title,
    onClose,
}: {
    programId: number;
    title: string;
    onClose: () => void;
}) {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const frameRef = useRef<HTMLDivElement | null>(null);
    const cached = getCachedAssignment(programId);
    const [kind, setKind] = useState<AssignmentKind | null>(cached?.kind || null);
    const [content, setContent] = useState<ArrayBuffer | string | null>(cached?.content || null);
    const [imageUrl, setImageUrl] = useState(cached?.imageUrl || "");
    const [error, setError] = useState("");
    const [fullscreen, setFullscreen] = useState(false);
    const [retryKey, setRetryKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        const force = retryKey > 0;
        const hit = force ? null : getCachedAssignment(programId);
        if (hit) {
            setKind(hit.kind);
            setContent(hit.content);
            setImageUrl(hit.imageUrl || "");
            setError("");
            return () => {
                cancelled = true;
                controller.abort();
            };
        }

        setKind(null);
        setContent(null);
        setImageUrl("");
        setError("");

        loadAssignment(programId, controller.signal, force)
            .then((payload) => {
                if (cancelled) return;
                setKind(payload.kind);
                setContent(payload.content);
                setImageUrl(payload.imageUrl || "");
            })
            .catch((e: any) => {
                if (cancelled || e?.name === "AbortError") return;
                setError(e?.message || "Could not open this assignment");
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [programId, retryKey]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const key = e.key.toLowerCase();
            if ((e.ctrlKey || e.metaKey) && (key === "s" || key === "p")) {
                e.preventDefault();
                e.stopPropagation();
            }
            if (key === "escape" && !document.fullscreenElement) onClose();
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [onClose]);

    useEffect(() => {
        const onChange = () => setFullscreen(document.fullscreenElement === frameRef.current);
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);

    const watermark = useMemo(() => {
        const name = [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(" ");
        return [name, userInfo?.email].filter(Boolean).join(" · ") || "RYD Learning";
    }, [userInfo]);
    const html = useMemo(
        () => (typeof content === "string" ? prepareHtmlForViewer(content, { watermark }) : ""),
        [content, watermark]
    );

    const toggleFullscreen = () => {
        if (document.fullscreenElement) document.exitFullscreen?.();
        else frameRef.current?.requestFullscreen?.().catch(() => undefined);
    };

    const view = (
        <div className="fixed inset-0 z-[1100] overflow-y-auto bg-[#f4f7fb]">
            <style>{`@media print { .assignment-viewer { display: none !important; } .assignment-print-note { display: block !important; } }`}</style>
            <p className="assignment-print-note hidden p-10 text-center">Assignments can only be viewed on the RYD parent site.</p>
            <div className="assignment-viewer mx-auto w-full max-w-6xl space-y-4 px-4 pb-10 pt-4 sm:px-6 md:pt-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex shrink-0 items-center gap-2 self-start rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
                    >
                        <X size={16} aria-hidden />
                        Close
                    </button>
                    <div className="min-w-0 flex-1">
                        <h1 className="truncate text-lg font-semibold text-gray-900 sm:text-xl">{title || "Assignment"}</h1>
                        <p className="text-xs text-gray-500">Assignment</p>
                    </div>
                    {content !== null && !error ? (
                        <button
                            type="button"
                            onClick={toggleFullscreen}
                            className="inline-flex shrink-0 items-center gap-2 self-start rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
                        >
                            {fullscreen ? <Minimize2 size={16} aria-hidden /> : <Maximize2 size={16} aria-hidden />}
                            {fullscreen ? "Exit full screen" : "Full screen"}
                        </button>
                    ) : null}
                </div>

                {error ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
                        <p>{error}</p>
                        <button
                            type="button"
                            className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-700"
                            onClick={() => setRetryKey((n) => n + 1)}
                        >
                            Try again
                        </button>
                    </div>
                ) : content === null ? (
                    <div className="flex h-[60vh] flex-col items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white">
                        <p className="flex items-center gap-2 text-sm text-gray-500">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-ryd-primary" aria-hidden />
                            Opening assignment…
                        </p>
                        <p className="text-xs text-gray-400">This usually takes a few seconds the first time.</p>
                    </div>
                ) : (
                    <div
                        ref={frameRef}
                        className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 select-none"
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                    >
                        {kind === "pdf" && content instanceof ArrayBuffer ? (
                            <div className={`relative ${fullscreen ? "h-screen" : "max-h-[80vh]"} overflow-y-auto p-2 sm:p-4`}>
                                <PdfViewer data={content} />
                                {/* Same-document overlay is fine with pointer-events: none; PDF link hit-areas sit under it. */}
                                <div aria-hidden className="pointer-events-none absolute inset-0 z-[1] select-none overflow-hidden" style={{ opacity: 0.07 }}>
                                    <div className="absolute -inset-1/2 flex rotate-[-24deg] flex-wrap content-start gap-x-16 gap-y-20 p-10 text-sm font-semibold text-gray-900">
                                        {Array.from({ length: 40 }).map((_, i) => (
                                            <span key={i} className="whitespace-nowrap">{watermark}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : kind === "image" ? (
                            <div className={`relative flex items-center justify-center bg-white p-4 ${fullscreen ? "h-screen" : "max-h-[80vh]"}`}>
                                <img src={imageUrl} alt={title || "Assignment"} className="max-h-full max-w-full object-contain" />
                                <div aria-hidden className="pointer-events-none absolute inset-0 select-none overflow-hidden" style={{ opacity: 0.07 }}>
                                    <div className="absolute -inset-1/2 flex rotate-[-24deg] flex-wrap content-start gap-x-16 gap-y-20 p-10 text-sm font-semibold text-gray-900">
                                        {Array.from({ length: 40 }).map((_, i) => (
                                            <span key={i} className="whitespace-nowrap">{watermark}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // No parent overlay on HTML: overlays above iframes often eat clicks (week tabs, buttons).
                            <iframe
                                title={title || "Assignment"}
                                srcDoc={html}
                                sandbox={HTML_SANDBOX}
                                className={`relative z-[1] block w-full bg-white ${fullscreen ? "h-screen" : "h-[80vh]"}`}
                            />
                        )}
                    </div>
                )}

                {kind === "html" && content !== null && !error ? (
                    <p className="text-xs text-gray-400">Anything you enter on this page isn't saved after you leave it.</p>
                ) : null}
            </div>
        </div>
    );

    if (typeof document === "undefined") return view;
    return createPortal(view, document.body);
}
